import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, X, Sparkles } from "lucide-react";

/* ────────────────────────────────────────────────────────────
 * Sticky exit-CTA bar
 *
 * Appears once a visitor has scrolled ≥ SCROLL_TRIGGER_PCT of the page,
 * on long-form pages where they're likely engaged (blog, case studies,
 * service, vertical, glossary entries, comparison, best-of, app categories).
 *
 * Hidden on:
 *  - Pages that ARE conversion pages (free audit, discovery, build-your-plan,
 *    contact, pricing, audit share)
 *  - The homepage (has its own CTAs)
 *  - 404 (handled separately)
 *  - The current target page itself (so we don't pitch a page from itself)
 *
 * Dismissal persists for 7 days in localStorage so we don't nag.
 * ──────────────────────────────────────────────────────────── */

const STORAGE_KEY = "appfillip_sticky_cta_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const SCROLL_TRIGGER_PCT = 0.45;

// Path prefixes where the sticky bar is suppressed.
const HIDE_ON_PREFIXES = [
  "/free-aso-audit",
  "/discovery",
  "/build-your-plan",
  "/contact",
  "/pricing",
  "/audit",
  "/audit-report",
  "/audit-package",
  "/audit/share",
  "/404",
];

// Exact paths where the sticky bar is suppressed.
const HIDE_ON_EXACT = ["/"];

function shouldShowOnPath(pathname) {
  if (HIDE_ON_EXACT.includes(pathname)) return false;
  if (HIDE_ON_PREFIXES.some((p) => pathname.startsWith(p))) return false;
  return true;
}

function isDismissed() {
  try {
    const at = localStorage.getItem(STORAGE_KEY);
    if (!at) return false;
    const ts = parseInt(at, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    /* private mode etc — fine to ignore */
  }
}

export default function StickyExitCTA() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Reset on route change
  useEffect(() => {
    setVisible(false);
    setDismissed(isDismissed());
  }, [location.pathname]);

  useEffect(() => {
    if (dismissed) return;
    if (!shouldShowOnPath(location.pathname)) return;

    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const doc = document.documentElement;
        const scrollTop = window.scrollY || doc.scrollTop;
        const max = (doc.scrollHeight || 1) - (window.innerHeight || 1);
        const pct = max > 0 ? scrollTop / max : 0;
        if (pct >= SCROLL_TRIGGER_PCT) {
          setVisible(true);
        }
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // check immediately in case the user landed mid-page

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [location.pathname, dismissed]);

  if (!shouldShowOnPath(location.pathname)) return null;
  if (dismissed) return null;

  // Personalize the CTA copy lightly by section
  const path = location.pathname;
  let eyebrow = "Free · 30-second audit";
  let title = "Get your app's free marketing audit";
  let subtitle = "ASO score + 3 quick wins · No signup required.";
  let primary = { to: "/free-aso-audit", label: "Run free audit" };
  let secondary = { to: "/discovery", label: "Discovery" };

  if (path.startsWith("/case-studies")) {
    title = "Want results like this for your app?";
    subtitle = "Tell us about your app — we'll model your projection.";
    primary = { to: "/discovery", label: "Start Discovery" };
    secondary = { to: "/free-aso-audit", label: "Free audit" };
    eyebrow = "Case study reader";
  } else if (path.startsWith("/blog")) {
    title = "Get this week's app marketing teardown";
    subtitle = "1 deep-dive case study + 3 wins, every Friday.";
    primary = { to: "/free-aso-audit", label: "Free audit" };
    secondary = { to: "/resources/roi-calculator", label: "ROI calculator" };
    eyebrow = "Reading: " + (path.split("/")[2] || "blog").replace(/-/g, " ").slice(0, 28);
  } else if (
    path.startsWith("/aso") ||
    path.startsWith("/user-acquisition") ||
    path.startsWith("/influencer-marketing") ||
    path.startsWith("/app-retention") ||
    path.startsWith("/ratings-reviews") ||
    path.startsWith("/creative-production") ||
    path.startsWith("/analytics") ||
    path.startsWith("/content-marketing") ||
    path.startsWith("/advanced-paid-marketing") ||
    path.startsWith("/gtm-strategy") ||
    path.startsWith("/international-expansion") ||
    path.startsWith("/consultancy") ||
    path.startsWith("/geo-ai-citation") ||
    path.startsWith("/app-development")
  ) {
    title = "See what we'd do for your app";
    subtitle = "Free 30-second audit — score + 3 quick wins.";
    primary = { to: "/free-aso-audit", label: "Free audit" };
    secondary = { to: "/discovery", label: "Talk to us" };
    eyebrow = "Service · " + path.replace("/", "").replace(/-/g, " ").slice(0, 28);
  } else if (
    path.endsWith("-app-marketing") ||
    path.startsWith("/app-marketing/")
  ) {
    title = "Ranked 100+ apps — yours next?";
    subtitle = "Free audit tailored to your category. 30 seconds, no signup.";
    primary = { to: "/free-aso-audit", label: "Free audit" };
    secondary = { to: "/discovery", label: "Discovery" };
    eyebrow = "Industry playbook";
  } else if (path.startsWith("/glossary")) {
    title = "Stop reading. Start ranking.";
    subtitle = "Run our free 30-second audit on your app right now.";
    primary = { to: "/free-aso-audit", label: "Free audit" };
    secondary = { to: "/resources/roi-calculator", label: "ROI calc" };
    eyebrow = "Glossary";
  } else if (path.startsWith("/resources/")) {
    title = "Like this tool? Wait till you see our work.";
    subtitle = "Run a free audit and we'll send a real teardown.";
    primary = { to: "/free-aso-audit", label: "Free audit" };
    secondary = { to: "/case-studies", label: "Case studies" };
    eyebrow = "Resources";
  } else if (path.startsWith("/appfillip-vs-")) {
    title = "Skip the comparison — let's just run an audit";
    subtitle = "30 seconds. We'll show you what's left on the table.";
    primary = { to: "/free-aso-audit", label: "Free audit" };
    secondary = { to: "/discovery", label: "Discovery" };
    eyebrow = "Comparison reader";
  }

  // Don't pitch the page that the user is already on
  if (primary.to === path) {
    primary = { to: "/discovery", label: "Discovery" };
  }
  if (secondary.to === path) {
    secondary = { to: "/case-studies", label: "Case studies" };
  }

  function handleDismiss() {
    setDismissed(true);
    setVisible(false);
    markDismissed();
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 28 }}
          className="fixed left-3 right-[80px] sm:left-1/2 sm:right-auto sm:-translate-x-1/2 bottom-3 sm:bottom-5 z-[55] max-w-2xl mx-auto sm:mx-0"
          role="dialog"
          aria-label="Get your free app marketing audit"
          data-testid="sticky-exit-cta"
        >
          <div className="relative bg-charcoal text-white border-2 border-electric shadow-[6px_6px_0_0_#2196F3]">
            {/* Subtle electric glow */}
            <motion.div
              className="absolute -inset-px bg-electric/10 blur-md pointer-events-none -z-10"
              animate={{ opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              aria-hidden
            />

            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss"
              className="absolute top-2 right-2 p-1.5 text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-4 pr-10 sm:p-5 sm:pr-12 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
              <div className="flex-1 min-w-0">
                <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-electric mb-1">
                  <Sparkles className="w-3 h-3" />
                  <span className="truncate">{eyebrow}</span>
                </div>
                <p className="font-display text-sm sm:text-base font-extrabold leading-tight">
                  {title}
                </p>
                <p className="text-[11px] sm:text-xs text-zinc-400 mt-0.5 leading-snug">
                  {subtitle}
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <Link
                  to={primary.to}
                  onClick={handleDismiss}
                  className="inline-flex items-center gap-1.5 bg-electric text-white px-3.5 py-2.5 text-xs sm:text-sm font-bold hover:bg-electric-400 transition-colors whitespace-nowrap"
                >
                  {primary.label}
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link
                  to={secondary.to}
                  onClick={handleDismiss}
                  className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold text-zinc-300 hover:text-electric px-2 py-2 transition-colors whitespace-nowrap"
                >
                  {secondary.label}
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
